import { useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { Button, Skeleton, RadioGroup, RadioGroupItem } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useWaapCurrentPackage,
  useChangeWaapPackage,
  useUpgradeWaapPlanBilling,
  isInsufficientCreditError,
  waapPlanMonthlyVnd,
  WAAP_PLANS,
  type WaapPlanFeatures,
} from '@/api/waap';
import { useCredits } from '@/api/billing';
import { TopUpDialog } from '@/pages/account/billing/TopUpDialog';
import { errMessage } from '../waap-util';
import { SaveBar } from './settings-ui';
import { PlanContactDialog } from './PlanContactDialog';
import styles from './PackageSettings.module.css';

// The full feature matrix shown on every plan card (mirrors the vendor's plan
// comparison). Only Bot Management / API Protection / Programmable Mitigation
// vary by tier; the rest ship on every plan.
const FEATURE_ROWS: { label: string; on: (f: WaapPlanFeatures) => boolean }[] = [
  { label: 'AI-WAF', on: () => true },
  { label: 'DDoS Mitigation', on: () => true },
  { label: 'Emergency Mitigation', on: () => true },
  { label: 'Bot Management', on: (f) => f.botManagement },
  { label: 'API Protection', on: (f) => f.apiProtection },
  { label: 'Programmable Mitigation', on: (f) => f.programmableMitigation },
  { label: 'Content Acceleration', on: () => true },
  { label: 'Global Load Balancing', on: () => true },
  { label: 'Administration & API', on: () => true },
  { label: 'Ticketing Support', on: () => true },
];

// Free is shown as a full-width row at the bottom (Cloudflare-style); the paid
// tiers fill the top comparison grid.
const FREE_PLAN = WAAP_PLANS.find((p) => p.id === '4001');
const PAID_PLANS = WAAP_PLANS.filter((p) => p.id !== '4001');

// The Custom (Enterprise) card highlights — a contact-sales tier, not a
// self-serve vendor package.
const CUSTOM_HIGHLIGHTS = [
  'Everything in Pro',
  'Custom domains, volume & RPS',
  'Custom limits & SLA',
  'Dedicated support',
];

/**
 * Subscription plan — pick a plan (Update) or request a Custom plan (contact
 * sales). Plans are shown as comparison cards. Service-scoped; the plan lives on
 * the WAAP customer entity. Free (4001) is the floor: there is no "no package"
 * state, so downgrading picks the Free card. Leaving WAAP entirely (and deleting
 * the service) lives in the Danger zone tab, not here.
 */
export function PackageSettings({ serviceId }: { serviceId: string }) {
  const { show } = useToast();
  const { data: currentId = '', isLoading } = useWaapCurrentPackage(serviceId);

  const currentValue = currentId;

  const [pkg, setPkg] = useState<string>(WAAP_PLANS[0].id);
  const [contactOpen, setContactOpen] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState(0);

  const { data: credits, refetch: refetchCredits } = useCredits();

  // Seed the selection from the server once (a current plan selects itself).
  const syncedRef = useRef(false);
  useEffect(() => {
    if (isLoading || syncedRef.current) return;
    syncedRef.current = true;
    if (currentValue) setPkg(currentValue);
  }, [isLoading, currentValue]);

  const change = useChangeWaapPackage(serviceId);

  const upgradeBilling = useUpgradeWaapPlanBilling(serviceId);

  // Order matters for a two-system change (vendor swap + wallet charge) with no
  // shared transaction: pre-check the balance, swap the vendor package FIRST
  // (@mgmt validates ownership + applies it), and only bill the wallet once the
  // swap actually succeeded. That way a rejected swap never leaves the customer
  // charged for a plan they didn't get. Free (4001) is 0đ, so a downgrade to Free
  // just swaps the package with no charge.
  const updatePlan = async () => {
    // Charge includes 10% VAT (applied server-side); the wallet must cover it.
    const chargeVnd = Math.ceil(waapPlanMonthlyVnd(pkg) * 1.1);
    const remaining = credits?.balance?.total_remaining ?? 0;
    if (chargeVnd > 0 && remaining < chargeVnd) {
      // Not enough yet → top up the shortfall first; nothing is changed/charged.
      setTopUpAmount(Math.max(chargeVnd - remaining, 0) || chargeVnd);
      setTopUpOpen(true);
      return;
    }

    try {
      await change.mutateAsync(pkg); // vendor swap — authoritative ownership check
    } catch (e) {
      show({ tone: 'danger', title: 'Update failed', description: errMessage(e) });
      return;
    }

    try {
      await upgradeBilling.mutateAsync(pkg); // bill the wallet after the swap
    } catch (e) {
      if (isInsufficientCreditError(e)) {
        // Balance was pre-checked, so this is a rare race — prompt a top-up.
        setTopUpAmount(chargeVnd);
        setTopUpOpen(true);
        return;
      }
      // Plan already swapped but billing failed — surface it for reconciliation.
      show({ tone: 'warning', title: 'Plan changed — billing pending', description: errMessage(e) });
      return;
    }
    show({ tone: 'success', title: 'Subscription plan updated' });
  };

  if (isLoading) return <Skeleton className={styles.skeleton} />;

  return (
    <div className={styles.stack}>
      <Section
        title="Subscription plan"
        description="Select a plan to change this WAAP service's subscription tier. Free is the lowest tier."
        style={{ marginBottom: 0 }}
      >
        <RadioGroup value={pkg} onValueChange={setPkg}>
          <div className={styles.cards}>
            {PAID_PLANS.map((p) => {
              const selected = pkg === p.id;
              return (
                <label
                  key={p.id}
                  className={`${styles.card}${selected ? ` ${styles.cardSelected}` : ''}`}
                >
                  <div className={styles.cardHead}>
                    <RadioGroupItem value={p.id} className={styles.radio} />
                    <div className={styles.cardTitle}>
                      <span className={styles.planName}>
                        {p.name}
                        {p.id === currentValue && (
                          <span className={styles.currentBadge}>Current</span>
                        )}
                      </span>
                      {p.price && <span className={styles.planPrice}>{p.price}</span>}
                    </div>
                  </div>
                  <div className={styles.specs}>
                    <span>{p.domains} domain(s)</span>
                    <span>{p.volume}/mo</span>
                    <span>{p.qps} QPS</span>
                  </div>
                  <ul className={styles.features}>
                    {FEATURE_ROWS.map((row) => {
                      const on = row.on(p.features);
                      return (
                        <li
                          key={row.label}
                          className={`${styles.feat} ${on ? styles.on : styles.off}`}
                        >
                          {on ? <Check size={14} /> : <X size={14} />}
                          {row.label}
                        </li>
                      );
                    })}
                  </ul>
                </label>
              );
            })}

            {/* Custom (Enterprise) — contact sales, not a self-serve package. */}
            <div
              className={`${styles.card} ${styles.customCard}`}
              role="button"
              tabIndex={0}
              onClick={() => setContactOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setContactOpen(true);
                }
              }}
            >
              <div className={styles.cardHead}>
                <div className={styles.cardTitle}>
                  <span className={styles.planName}>Custom</span>
                  <span className={styles.planPrice}>Contact us</span>
                </div>
              </div>
              <div className={styles.specs}>
                <span>Tailored to your needs</span>
              </div>
              <ul className={styles.features}>
                {CUSTOM_HIGHLIGHTS.map((h) => (
                  <li key={h} className={`${styles.feat} ${styles.on}`}>
                    <Check size={14} />
                    {h}
                  </li>
                ))}
              </ul>
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setContactOpen(true);
                }}
              >
                Talk to an expert
              </Button>
            </div>
          </div>

          {/* Free — a full-width row at the bottom (no price), Cloudflare-style. */}
          {FREE_PLAN && (
            <label
              className={`${styles.card} ${styles.freeCard}${
                pkg === FREE_PLAN.id ? ` ${styles.cardSelected}` : ''
              }`}
            >
              <div className={styles.freeHead}>
                <RadioGroupItem value={FREE_PLAN.id} className={styles.radio} />
                <span className={styles.planName}>
                  {FREE_PLAN.name}
                  {FREE_PLAN.id === currentValue && (
                    <span className={styles.currentBadge}>Current</span>
                  )}
                </span>
                <span className={styles.specs}>
                  <span>{FREE_PLAN.domains} domain(s)</span>
                  <span>{FREE_PLAN.volume}/mo</span>
                  <span>{FREE_PLAN.qps} QPS</span>
                </span>
              </div>
              <ul className={`${styles.features} ${styles.freeFeatures}`}>
                {FEATURE_ROWS.map((row) => {
                  const on = row.on(FREE_PLAN.features);
                  return (
                    <li
                      key={row.label}
                      className={`${styles.feat} ${on ? styles.on : styles.off}`}
                    >
                      {on ? <Check size={14} /> : <X size={14} />}
                      {row.label}
                    </li>
                  );
                })}
              </ul>
            </label>
          )}
        </RadioGroup>
        <SaveBar
          onSave={updatePlan}
          saving={upgradeBilling.isPending || change.isPending}
          disabled={pkg === currentValue}
          label="Update plan"
        />
      </Section>

      <PlanContactDialog open={contactOpen} onOpenChange={setContactOpen} />

      <TopUpDialog
        open={topUpOpen}
        onOpenChange={setTopUpOpen}
        defaultAmount={topUpAmount}
        title="Top up to upgrade"
        description="Your wallet can't cover this plan yet. Add funds, then select the plan again to complete the upgrade."
        onClosed={() => {
          // Refresh the balance so a funded wallet lets the retry succeed.
          refetchCredits();
          show({
            tone: 'info',
            title: 'Wallet updated',
            description: 'If your top-up went through, click Update plan again to finish the upgrade.',
          });
        }}
      />
    </div>
  );
}
